import { computed, ref, toValue, type MaybeRefOrGetter } from 'vue';
import {
  keepPreviousData,
  queryOptions,
  useMutation,
  useQueryClient,
  type QueryClient,
  type QueryKey,
} from '@tanstack/vue-query';
import type { CadFile } from '@cutr/constants/order';
import type { SettingsKey } from '@cutr/constants/files';
import {
  deleteOrderFile,
  getArchivedOrderFiles,
  getOrderFile,
  getOrderFiles,
  getReplacedRevisions,
  markOrderFileViewed,
  mergeOrderFiles,
  patchOrderFile,
  patchOrderFilesSettings,
  restoreOrderFile,
  reviseOrderFile,
  uploadOrderFile,
  type UploadOrderFileOptions,
} from '@/api/orderFiles';
import { isRunning } from '@/helpers/cadfile-status';
import { appendRow, patchRow, removeRow } from './shared/listUpdaters';
import { LIST_STALE_TIME_MS } from './shared/staleTimes';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId, QueryFlags, WithVariant } from './shared/types';
import { pick } from '@/helpers/helpers';
import {
  commentKeys,
  isOrderFilesKey,
  orderFileKeys,
  materialRequestKeys,
  orderFolderKeys,
  orderHardwareKeys,
  orderVariantKeys,
  partKeys,
  partOperationKeys,
} from './keys';

type UpdateFileVariables = { id: string; values: Partial<CadFile> };

export type CadFilePatch = Partial<CadFile> & Pick<CadFile, 'id'>;
export type UploadFileVariables = UploadOrderFileOptions & {
  formData: FormData;
};

export type ReviseFileVariables = UploadFileVariables & {
  originalFileId: string;
};

export type MultiSettingsPatch = Partial<
  Record<SettingsKey, boolean> & {
    quantity: number;
    assetType: CadFile['assetType'];
  }
>;

export type InvalidateFilesOptions = { parts?: boolean };

/** Both ids, because the files hang off the variant and the comments off the order. */
type InvalidateFilesTarget = InvalidateFilesOptions & {
  orderId: string | null | undefined;
  variantId: string | null | undefined;
};

export type MergeFilesVariables = { targetFileId: string; fileIds: string[] };

export const ANALYSIS_POLL_INTERVAL_MS = 3000;
export const ANALYSIS_TIMEOUT_MS = 3 * 60_000;
const EDIT_DEBOUNCE_MS = 500;

export function orderFilesListOptions(variantId: string) {
  return queryOptions({
    queryKey: orderFileKeys.list(variantId),
    queryFn: ({ signal }) => getOrderFiles(variantId, signal),
    staleTime: LIST_STALE_TIME_MS,
  });
}

export function archivedOrderFilesOptions(variantId: string) {
  return queryOptions({
    queryKey: orderFileKeys.archived(variantId),
    queryFn: ({ signal }) => getArchivedOrderFiles(variantId, signal),
    staleTime: LIST_STALE_TIME_MS,
  });
}

export function replacedRevisionsOptions(variantId: string) {
  return queryOptions({
    queryKey: orderFileKeys.replaced(variantId),
    queryFn: ({ signal }) => getReplacedRevisions(variantId, signal),
    staleTime: LIST_STALE_TIME_MS,
  });
}

export function computeRefetchInterval(
  files: readonly CadFile[] | undefined,
  timedOutIds: ReadonlySet<string>
) {
  const running = files?.some(
    (file) => isRunning(file) && !timedOutIds.has(file.id)
  );
  return running ? ANALYSIS_POLL_INTERVAL_MS : false;
}

export function useArchivedOrderFilesQuery(
  variantId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(variantId, archivedOrderFilesOptions, flags);

  return { query, archivedFiles: computed(() => query.data.value ?? []) };
}

export function useReplacedRevisionsQuery(
  variantId: MaybeId,
  flags?: QueryFlags
) {
  const query = useScopedQuery(variantId, replacedRevisionsOptions, flags);

  return { query, replacedRevisions: computed(() => query.data.value ?? []) };
}

export function useOrderFilesQuery(
  variantId: MaybeId,
  {
    timedOutIds = new Set<string>(),
    ...flags
  }: QueryFlags & { timedOutIds?: MaybeRefOrGetter<ReadonlySet<string>> } = {}
) {
  const query = useScopedQuery(variantId, orderFilesListOptions, {
    ...flags,
    placeholderData: keepPreviousData,
    refetchInterval: (query) =>
      computeRefetchInterval(query.state.data, toValue(timedOutIds)),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: (query) =>
      query.state.data?.some(isRunning) ? 'always' : false,
  });

  return { query, files: computed(() => query.data.value ?? []) };
}

// Every cached list (main, archived, replaced, any variant) holding the file.
export function cachedListsWith(queryClient: QueryClient, fileId: string) {
  return queryClient
    .getQueriesData<CadFile[]>({
      predicate: ({ queryKey }) => isOrderFilesKey(queryKey),
    })
    .filter(([, list]) => list?.some((file) => file.id === fileId));
}

export function findCachedFile(queryClient: QueryClient, fileId: string) {
  const [, list] = cachedListsWith(queryClient, fileId)[0] ?? [];
  return list?.find((file) => file.id === fileId);
}

export function setCachedLists(
  queryClient: QueryClient,
  fileId: string,
  updater: (files: CadFile[]) => CadFile[] | undefined
) {
  cachedListsWith(queryClient, fileId).forEach(([queryKey]) =>
    queryClient.setQueryData<CadFile[]>(
      queryKey,
      (current) => current && updater(current)
    )
  );
}

export function cancelListsWith(queryClient: QueryClient, fileId: string) {
  return Promise.all(
    cachedListsWith(queryClient, fileId).map(([queryKey]) =>
      queryClient.cancelQueries({ queryKey })
    )
  );
}

export function patchCachedFile(queryClient: QueryClient, patch: CadFilePatch) {
  setCachedLists(queryClient, patch.id, patchRow<CadFile>(patch));
}

export function addCachedFile(queryClient: QueryClient, file: CadFile) {
  queryClient.setQueryData<CadFile[]>(
    orderFileKeys.list(file.orderVariantId),
    appendRow(file)
  );
}

export function removeCachedFile(queryClient: QueryClient, fileId: string) {
  setCachedLists(queryClient, fileId, removeRow(fileId));
}

type QuotingFileEntry = NonNullable<CadFile['quotingFiles']>[number];

export type QuotingFile = CadFile & Pick<QuotingFileEntry, 'referenceMarker'>;

/**
 * The parent PDF carries a denormalised list of its quoting children, and no write
 * endpoint answers with the parent. Absent means this session did not get the
 * include, or the parent is not cached: leave it absent rather than invent a
 * one-element array that reads as "this PDF has exactly one child".
 */
function setQuotingFiles(
  queryClient: QueryClient,
  parentId: string,
  updater: (entries: QuotingFileEntry[]) => QuotingFileEntry[]
) {
  const quotingFiles = findCachedFile(queryClient, parentId)?.quotingFiles;
  if (!quotingFiles) return;
  patchCachedFile(queryClient, {
    id: parentId,
    quotingFiles: updater([...quotingFiles]),
  });
}

export function linkQuotingFile(
  queryClient: QueryClient,
  { parentId, child }: { parentId: string; child: QuotingFileEntry }
) {
  setQuotingFiles(queryClient, parentId, (entries) =>
    entries.some((entry) => entry.id === child.id)
      ? entries
      : [...entries, child]
  );
}

export function relinkQuotingFile(
  queryClient: QueryClient,
  { parentId, child }: { parentId: string; child: QuotingFileEntry }
) {
  setQuotingFiles(queryClient, parentId, (entries) =>
    entries.map((entry) =>
      entry.id === child.id ? { ...entry, ...child } : entry
    )
  );
}

export function unlinkQuotingFile(
  queryClient: QueryClient,
  { parentId, quotingFileId }: { parentId: string; quotingFileId: string }
) {
  setQuotingFiles(queryClient, parentId, (entries) =>
    entries.filter((entry) => entry.id !== quotingFileId)
  );
}

/**
 * The one entry point for "the files of this variant changed".
 *
 * The order's comments come along because the writers that reach here revise the
 * file server-side, and a revision carries its conversations onto the new one.
 * The material requests follow the parts: regenerating them rewrites which parts
 * each request holds.
 * Pass `{ parts: false }` only where the parts are demonstrably untouched.
 */
export async function invalidateOrderFiles(
  queryClient: QueryClient,
  { orderId, variantId, parts = true }: InvalidateFilesTarget
) {
  if (!variantId) return;
  const invalidate = <T extends QueryKey>(queryKey: T) =>
    queryClient.invalidateQueries({ queryKey });

  // Flagged first and left unawaited: the caller awaits this function to read the
  // list back, and waiting on a parts refetch to start one would hold that up.
  if (parts) {
    invalidate(partKeys.all(variantId));
    invalidate(partOperationKeys.list(variantId));
    invalidate(orderHardwareKeys.list(variantId));
    invalidate(materialRequestKeys.list(variantId));
  }

  orderId && invalidate(commentKeys.list(orderId));

  await Promise.all([
    invalidate(orderFileKeys.all(variantId)),
    invalidate(orderFolderKeys.structure(variantId)),
  ]);
}

/**
 * For an order unlock or a variant switch, where the client cannot derive what changed.
 */
export async function invalidateOrderVariantData(
  queryClient: QueryClient,
  variantId: string | null | undefined
) {
  if (!variantId) return;

  await queryClient.invalidateQueries({
    queryKey: orderVariantKeys.all(variantId),
  });
}

export function useInvalidateOrderFiles(orderId: MaybeId) {
  const queryClient = useQueryClient();

  return {
    invalidateFiles: (
      variantId: string | null | undefined,
      options?: InvalidateFilesOptions
    ) =>
      invalidateOrderFiles(queryClient, {
        orderId: toValue(orderId),
        variantId,
        ...options,
      }),
    invalidateVariantData: (variantId: string | null | undefined) =>
      invalidateOrderVariantData(queryClient, variantId),
  };
}

export function useOrderFileMutations(orderId: MaybeId) {
  const queryClient = useQueryClient();

  const filesViewed = ref<Record<string, boolean>>({});

  // Field patches whose POST has not settled yet.
  // A list fetch that started before the POST committed can resolve after it.
  const pendingPatches = new Map<string, Partial<CadFile>>();
  const inFlightWrites = new Map<string, number>();
  const needsReconcile = new Set<string>();

  function withPendingPatches(list: CadFile[]) {
    if (!pendingPatches.size) return list;
    return list.map((file) => {
      const pending = pendingPatches.get(file.id);
      return pending ? { ...file, ...pending } : file;
    });
  }

  const ownedFields = (id: string, values: Partial<CadFile>) =>
    (Object.keys(values) as (keyof CadFile)[]).filter(
      (key) => pendingPatches.get(id)?.[key] === values[key]
    );

  function trackPendingPatch(id: string, values: Partial<CadFile>) {
    pendingPatches.set(id, { ...pendingPatches.get(id), ...values });
  }

  function settlePendingPatch(id: string, values: Partial<CadFile>) {
    const pending = pendingPatches.get(id);
    if (!pending) return;
    ownedFields(id, values).forEach((key) => delete pending[key]);
    if (!Object.keys(pending).length) pendingPatches.delete(id);
  }

  function trackInFlightWrite(id: string) {
    inFlightWrites.set(id, (inFlightWrites.get(id) ?? 0) + 1);
  }

  function releaseInFlightWrite(id: string) {
    const remaining = (inFlightWrites.get(id) ?? 1) - 1;
    if (remaining) return inFlightWrites.set(id, remaining);
    inFlightWrites.delete(id);
    if (needsReconcile.delete(id)) refreshFile(id).catch(() => undefined);
  }

  const invalidateFiles = (variantId: string) =>
    invalidateOrderFiles(queryClient, {
      orderId: toValue(orderId),
      variantId,
    });

  const patch = useMutation({
    mutationFn: ({ id, values }: UpdateFileVariables) =>
      patchOrderFile(id, values),
    onMutate: ({ id, values }) => {
      trackPendingPatch(id, values);
      trackInFlightWrite(id);
      patchCachedFile(queryClient, { ...values, id });
    },
    onSuccess: async (saved, { id, values }) => {
      await cancelListsWith(queryClient, id);
      patchCachedFile(queryClient, {
        ...pick(saved, ownedFields(id, values)),
        id,
      });
    },
    onError: (_error, { id }) => {
      needsReconcile.add(id);
    },
    onSettled: (_data, _error, { id, values }) => {
      settlePendingPatch(id, values);
      releaseInFlightWrite(id);
    },
  });

  const scheduledEdits = new Map<
    string,
    {
      timer: ReturnType<typeof setTimeout>;
      values: Partial<CadFile>;
      onError?: (error: unknown) => void;
    }
  >();

  function flushEdit(id: string) {
    const edit = scheduledEdits.get(id);
    if (!edit) return;
    clearTimeout(edit.timer);
    scheduledEdits.delete(id);
    patch
      .mutateAsync({ id, values: edit.values })
      .catch((error) => edit.onError?.(error));
  }

  /**
   * For a field the user edits live: the value is staged on the keystroke and only
   * the request waits out the quiet period, so a list refetch in between cannot
   * show the old one. One entry per file, so two fields edited together go out as
   * one request and one row's typing never cancels another's.
   */
  function scheduleFileEdit({
    id,
    values,
    onError,
  }: {
    id: string;
    values: Partial<CadFile>;
    onError?: (error: unknown) => void;
  }) {
    trackPendingPatch(id, values);
    patchCachedFile(queryClient, { ...values, id });

    const scheduled = scheduledEdits.get(id);
    if (scheduled) clearTimeout(scheduled.timer);
    scheduledEdits.set(id, {
      values: { ...scheduled?.values, ...values },
      onError,
      timer: setTimeout(() => flushEdit(id), EDIT_DEBOUNCE_MS),
    });
  }

  /**
   * There is no cancel path, only this one: a staged patch that never reached the
   * server would mask it until the page reloads.
   */
  function flushFileEdits() {
    [...scheduledEdits.keys()].forEach(flushEdit);
  }

  const patchSettings = useMutation({
    mutationFn: ({
      fileIds,
      settings,
    }: {
      fileIds: string[];
      settings: MultiSettingsPatch;
    }) => patchOrderFilesSettings(fileIds, settings),
    onSuccess: async (files) => {
      const variantIds = new Set(files.map((file) => file.orderVariantId));
      await Promise.all(
        [...variantIds].map((variantId) =>
          queryClient.cancelQueries({ queryKey: orderFileKeys.list(variantId) })
        )
      );
      // The settings endpoint returns a different shape; only the fields the
      // list already carries are written back.
      files.forEach((file) => {
        const cached = findCachedFile(queryClient, file.id);
        if (!cached) return;
        patchCachedFile(
          queryClient,
          pick(file, Object.keys(cached) as (keyof CadFile)[])
        );
      });
    },
  });

  const markViewed = useMutation({
    mutationFn: (fileId: string) => markOrderFileViewed(fileId),
    onMutate: (fileId) => {
      const isNew = findCachedFile(queryClient, fileId)?.isNew;
      filesViewed.value[fileId] = true;
      patchCachedFile(queryClient, { id: fileId, isNew: false });
      return { isNew };
    },
    onError: (_error, fileId, context) => {
      delete filesViewed.value[fileId];
      patchCachedFile(queryClient, { id: fileId, isNew: context?.isNew });
    },
  });

  const restoreMutation = useMutation({
    mutationFn: (fileId: string) => restoreOrderFile(fileId),
    onMutate: (fileId) => ({
      variantId: findCachedFile(queryClient, fileId)?.orderVariantId,
    }),
    onSuccess: (_data, _fileId, context) => {
      if (!context?.variantId) return;
      return invalidateFiles(context.variantId);
    },
  });

  const merge = useMutation({
    mutationFn: ({ targetFileId, fileIds }: WithVariant<MergeFilesVariables>) =>
      mergeOrderFiles(targetFileId, fileIds),
    onSuccess: (_data, { orderVariantId }) => invalidateFiles(orderVariantId),
  });

  // One mutation for the whole batch.
  // a partial failure resolves with the failed ids instead of rejecting.
  const removeMany = useMutation({
    mutationFn: async (fileIds: string[]) => {
      const results = await Promise.allSettled(
        fileIds.map((id) => deleteOrderFile(id))
      );
      return fileIds.filter((_, index) => results[index].status === 'rejected');
    },
    onMutate: async (fileIds) => {
      const variantIds = new Set(
        fileIds.flatMap(
          (id) => findCachedFile(queryClient, id)?.orderVariantId ?? []
        )
      );
      await Promise.all(fileIds.map((id) => cancelListsWith(queryClient, id)));
      fileIds.forEach((id) => removeCachedFile(queryClient, id));
      return { variantIds };
    },
    onSettled: (_failed, _error, _fileIds, context) =>
      Promise.all([...(context?.variantIds ?? [])].map(invalidateFiles)),
  });

  const upload = useMutation({
    mutationFn: ({ formData, onUploadProgress }: UploadFileVariables) =>
      uploadOrderFile(formData, { onUploadProgress }),
    // Appending first lets the caller select the new file before the refetch lands.
    onSuccess: (file) => {
      addCachedFile(queryClient, file);
      invalidateFiles(file.orderVariantId);
    },
  });

  const revise = useMutation({
    mutationFn: ({
      originalFileId,
      formData,
      onUploadProgress,
    }: ReviseFileVariables) =>
      reviseOrderFile(originalFileId, formData, { onUploadProgress }),
    onSuccess: (file) => invalidateFiles(file.orderVariantId),
  });

  /** Utility methods */

  async function refreshFile(fileId: string) {
    const file = await getOrderFile(fileId);
    await cancelListsWith(queryClient, fileId);
    patchCachedFile(queryClient, file);
  }

  // The thumbnail endpoints answer with the whole file; merging the rest of it
  // back would restore fields another edit just changed.
  function setFileThumbnails({
    fileId,
    thumbnails,
  }: {
    fileId: string;
    thumbnails: CadFile['thumbnails'];
  }) {
    patchCachedFile(queryClient, { id: fileId, thumbnails });
  }

  const quotingEntry = (file: QuotingFile): QuotingFileEntry => ({
    ...pick(file, ['id', 'name', 'filetype', 'shortId']),
    referenceMarker: file.referenceMarker,
  });

  function addQuotingFile({
    parentId,
    quotingFile,
  }: {
    parentId: string;
    quotingFile: QuotingFile;
  }) {
    addCachedFile(queryClient, quotingFile);
    linkQuotingFile(queryClient, {
      parentId,
      child: quotingEntry(quotingFile),
    });
  }

  function updateQuotingFile({
    parentId,
    quotingFile,
  }: {
    parentId: string;
    quotingFile: QuotingFile;
  }) {
    patchCachedFile(queryClient, quotingFile);
    relinkQuotingFile(queryClient, {
      parentId,
      child: quotingEntry(quotingFile),
    });
  }

  function removeQuotingFile({
    parentId,
    quotingFileId,
  }: {
    parentId: string;
    quotingFileId: string;
  }) {
    removeCachedFile(queryClient, quotingFileId);
    unlinkQuotingFile(queryClient, { parentId, quotingFileId });
  }

  async function updateFile(
    filePatch: CadFilePatch,
    fields: (keyof CadFile)[]
  ) {
    await patch.mutateAsync({
      id: filePatch.id,
      values: pick(filePatch, fields),
    });
  }

  function rename(file: CadFile, name: string) {
    return updateFile({ id: file.id, name }, ['name']);
  }

  function updateThickness(file: CadFile, thicknessUM: number) {
    return updateFile({ id: file.id, thicknessUM }, ['thicknessUM']);
  }

  function updateUpAxis(file: Pick<CadFile, 'id'>, up: string) {
    return updateFile({ id: file.id, up }, ['up']);
  }

  function updateSettings(
    file: CadFile,
    settings: Partial<Record<SettingsKey, boolean>> = {}
  ) {
    const next = { ...file, ...settings };
    const values: Partial<CadFile> = { ...settings };
    if (next.useForQuoting || next.useForWorkPrep) {
      values.visibleToCustomer = true;
      values.visibleToProductionPartner = true;
    }
    if (next.assetType !== 'production') {
      values.assetType = next.useForQuoting ? 'quoting' : 'design';
    }

    const fields = Object.keys(values) as (keyof CadFile)[];
    if (!fields.length) return Promise.resolve();
    return updateFile({ ...values, id: file.id }, fields);
  }

  async function updateMultipleSettings(
    fileIds: string[],
    settings: MultiSettingsPatch
  ) {
    await patchSettings.mutateAsync({ fileIds, settings });
  }

  function viewed(fileId: string) {
    if (filesViewed.value[fileId]) return Promise.resolve();
    if (!findCachedFile(queryClient, fileId)) return Promise.resolve();
    return markViewed.mutateAsync(fileId).then(
      () => undefined,
      () => console.error('Error marking file viewed', fileId)
    );
  }

  function uploadFile(variables: UploadFileVariables) {
    return upload.mutateAsync(variables);
  }

  function reviseFile(variables: ReviseFileVariables) {
    return revise.mutateAsync(variables);
  }

  function removeFiles(fileIds: string[]) {
    return removeMany.mutateAsync(fileIds);
  }

  async function restore(fileId: string) {
    await restoreMutation.mutateAsync(fileId);
  }

  async function mergeFiles(variables: MergeFilesVariables) {
    const target = findCachedFile(queryClient, variables.targetFileId);
    if (!target) return Promise.reject(new Error('Target file not found'));
    await merge.mutateAsync({
      ...variables,
      orderVariantId: target.orderVariantId,
    });
  }

  return {
    withPendingPatches,
    filesViewed,
    refreshFile,
    setFileThumbnails,
    scheduleFileEdit,
    flushFileEdits,
    addQuotingFile,
    updateQuotingFile,
    removeQuotingFile,
    updateFile,
    rename,
    updateThickness,
    updateUpAxis,
    updateSettings,
    updateMultipleSettings,
    viewed,
    restore,
    mergeFiles,
    removeFiles,
    uploadFile,
    reviseFile,
  };
}
